/**
 * AggregationControls Component
 * Checkbox group for selecting aggregation dimensions
 *
 * @module MetricDataDevTool/components/AggregationControls
 */

import React from 'react';
import {
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Box
} from '@mui/material';
import { AGGREGATION_OPTIONS } from '../constants';

/**
 * AggregationControls component
 * @param {Object} props - Component props
 * @param {Object} props.aggregationConfig - Current aggregation configuration
 * @param {boolean} props.aggregationConfig.byDate - Aggregate by date
 * @param {boolean} props.aggregationConfig.byDayPart - Aggregate by day part
 * @param {boolean} props.aggregationConfig.byDiningOption - Aggregate by dining option
 * @param {Function} props.onChange - Callback when aggregation config changes
 * @param {boolean} [props.disabled=false] - Whether controls are disabled
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.label='Aggregate Data By'] - Label for the control group
 */
export default function AggregationControls({
  aggregationConfig = {
    byDate: false,
    byDayPart: false,
    byDiningOption: false
  },
  onChange,
  disabled = false,
  error = null,
  label = 'Aggregate Data By'
}) {
  const handleChange = (dimension) => (event) => {
    const newConfig = {
      ...aggregationConfig,
      [dimension]: event.target.checked
    };
    onChange(newConfig);
  };

  return (
    <FormControl
      component="fieldset"
      error={!!error}
      disabled={disabled}
      fullWidth
    >
      <FormLabel component="legend" sx={{ mb: 1 }}>
        {label}
      </FormLabel>
      <FormGroup>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={aggregationConfig.byDate}
                onChange={handleChange('byDate')}
                name="byDate"
                disabled={disabled}
              />
            }
            label="By Date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={aggregationConfig.byDayPart}
                onChange={handleChange('byDayPart')}
                name="byDayPart"
                disabled={disabled}
              />
            }
            label="By Day Part"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={aggregationConfig.byDiningOption}
                onChange={handleChange('byDiningOption')}
                name="byDiningOption"
                disabled={disabled}
              />
            }
            label="By Dining Option"
          />
        </Box>
      </FormGroup>
      {error && <FormHelperText>{error}</FormHelperText>}
      {!error && (aggregationConfig.byDate || aggregationConfig.byDayPart || aggregationConfig.byDiningOption) && (
        <FormHelperText>
          {getAggregationSummary(aggregationConfig)}
        </FormHelperText>
      )}
    </FormControl>
  );
}

/**
 * Get human-readable summary of aggregation configuration
 * @param {Object} config - Aggregation configuration
 * @returns {string} Summary text
 */
function getAggregationSummary(config) {
  const dimensions = [];
  if (config.byDate) dimensions.push('Date');
  if (config.byDayPart) dimensions.push('Day Part');
  if (config.byDiningOption) dimensions.push('Dining Option');

  if (dimensions.length === 0) {
    return 'No aggregation selected';
  }

  return `Aggregating by: ${dimensions.join(', ')}`;
}
